import { defineStore } from "pinia";
import { questionnaireService } from "@/utils/questionnaireRequest";
import router from "../routes";
import moment from "moment";
import { useCommonStore } from "@/stores/CommonStore.ts";

export const useCardioQnrStore = defineStore("CardioQnrStore", {
  state: () => {
    return {
      birthdate: "",
      qnrIsCompleted: false,
      cardioQnr: {
        basic_info: {
          gender: null,
          birthdate: null,
          ethnicity: null,
          height: null,
          weight: null,
          specific_asian_ethnicity: null,
          other_ethnicity: null,
        },
        cardiac_health_symptoms: {
          chest_pain: null,
          shortness_of_breath: null,
          fatigue: null,
          lightheadedness: null,
          elevated_systemic_blood_pressure: null,
          irregular_heartbeat: null,
          palpitations: null,
          dizziness: null,
        },
        systemic_symptoms: {
          skin_hyperpigmentation: null,
          weight_fluctuations: null,
          persistent_hunger: null,
          edema: null,
          urinary_changes: null,
          brain_fog: null,
          jaundice: null,
          abdominal_pain: null,
          frequent_infections: null,
        },
        medical_history: {
          personal_history: {
            personal_cardiovascular_disease: null,
            personal_high_blood_pressure: null,
            personal_high_cholesterol: null,
            personal_atherosclerosis: null,
            personal_peripheral_arterial_disease: null,
            personal_diabetes: null,
            personal_insulin_resistance: null,
            personal_kidney_disease: null,
            personal_liver_disease: null,
            other_personal_conditions: null,
          },
          family_history: {
            family_cardiovascular_disease: null,
            family_high_blood_pressure: null,
            family_high_cholesterol: null,
            family_atherosclerosis: null,
            family_peripheral_arterial_disease: null,
            family_diabetes: null,
            family_insulin_resistance: null,
            family_kidney_disease: null,
            family_liver_disease: null,
            other_family_conditions: null,
          },
          blood_pressure_checked: null,
          blood_pressure_check_details: null,
          systolic: null,
          diastolic: null,
          has_heart_surgeries: null,
          heart_surgery_details: null,
          on_medications: null,
          medication_details: null,
          on_supplements: null,
          supplement_details: null,
        },
        dietary_patterns: {
          high_ultra_processed_diet: null,
          balanced_diet: null,
          high_fat_diet: null,
        },
        lifestyle: {
          active_level: null,
          physical_activity_frequency: null,
          physical_activities: {
            aerobic_exercise: null,
            strength_training: null,
            balance_training: null,
          },
          sleep_hours: null,
          stress_level: null,
          stress_reduction_frequency: null,
          alcohol_use_frequency: null,
          nicotine_use: null,
          stress_or_mental_health_issues: null,
        },
      },
      personalHistoryArray: [],
      familyHistoryArray: [],
      physicalActivityTypeArray: [],
      loadingQuestionnaire: false,
      loadingSubmit: false,
      patientHasDOB: false,
      patientHasGender: false,
    };
  },
  getters: {
    accession_id() {
      const commonStore = useCommonStore();
      return commonStore.accession_id;
    },
  },
  actions: {
    setCardioQnr(val) {
      if (!val || typeof val !== "object") return;
      // Filter out any keys that are not in the template
      const mergeValues = (source, template) => {
        let result = { ...template };

        for (const key in template) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            if (typeof template[key] === "object" && template[key] !== null) {
              result[key] = mergeValues(source[key], template[key]);
            } else {
              result[key] = source[key];
            }
          }
        }
        return result;
      };

      this.cardioQnr = mergeValues(val, this.cardioQnr);
      this.convertCardioQnrObjectToArray();
    },
    async getCardioQnrData() {
      if (!this.accession_id) {
        router.push(`/auth/CardioZoomer`);
        return;
      }
      const params = {
        accession_id: String(this.accession_id),
      };
      this.loadingQuestionnaire = true;
      try {
        const res = await questionnaireService.get(
          "/orders/samples/cardio-zoomer/questionnaires",
          {
            params: params,
          }
        );
        if (res.data?.basic_info?.gender) {
          this.setGender(res.data?.basic_info?.gender);
          this.patientHasGender = true;
        }
        if (res.data?.basic_info.birthdate) {
          this.dateConversion("birthdate", res.data?.basic_info?.birthdate);
          this.patientHasDOB = true;
        }
        if (res.data.completed_at) {
          this.setQnrIsCompleted(true);
        }
        this.setCardioQnr(res.data);
        this.loadingQuestionnaire = false;
        return Promise.resolve(res.data);
      } catch (err) {
        console.log("failed to retrieve questionnaire", err);
        router.push(`/auth/CardioZoomer/${this.accession_id}`);
        this.loadingQuestionnaire = false;
        return Promise.reject(err);
      }
    },
    submitCardioQnr(val) {
      let params = { ...this.cardioQnr };
      params.accession_id = this.accession_id;
      params.save_for_now = val;
      console.log("submitCardioQnr", params);     
      this.loadingSubmit = true;
      questionnaireService
        .post("/orders/samples/cardio-zoomer/questionnaires", params)
        .then(() => {
          this.loadingSubmit = false;
          if (!val) {
            router.push("/questionnaire_completed");
          }
        })
        .catch((err) => {
          this.loadingSubmit = false;
          router.push("/cardioZoomerFailed");
          console.log("submitQuestionnaire", err);
        });
    },
    // Set functions
    setGender(val) {
      this.cardioQnr.basic_info.gender = val;
    },
    setQnrIsCompleted(val) {
      const role = sessionStorage.getItem("role");
      this.qnrIsCompleted = role && role === "internal" ? false : val;
    },
    setCardioQnrSingleField(key, val) {
      // Split the provided key into an array by the dot (.) separator to handle nested keys
      const keys = key.split(".");
      // Start from the root object (cardioQnr)
      let currentObj = this.cardioQnr;
      // Iterate through all keys except the last one, to navigate through the nested objects
      for (let i = 0; i < keys.length - 1; i++) {
        // Move deeper into the object at each level
        if (!currentObj[keys[i]]) {
          // If the key doesn't exist at the current level, initialize it as an empty object
          currentObj[keys[i]] = {};
        }
        currentObj = currentObj[keys[i]];
      }
      // Update the value at the final key
      currentObj[keys[keys.length - 1]] = val;
    },
    setPersonalHistoryArray(val) {
      this.personalHistoryArray = val;
    },
    setFamilyHistoryArray(val) {
      this.familyHistoryArray = val;
    },
    setPhysicalActivityTypeArray(val) {
      this.physicalActivityTypeArray = val;
    },
    convertCardioQnrObjectToArray() {
      this.personalHistoryArray = [];
      this.familyHistoryArray = [];
      this.physicalActivityTypeArray = [];
      for (const key in this.cardioQnr.lifestyle?.physical_activities) {
        if (this.cardioQnr.lifestyle?.physical_activities[key]) {
          this.physicalActivityTypeArray.push(key);
        }
      }
      // Iterate through the medical history object and push the keys that have a truthy value to the corresponding array
      for (const key in this.cardioQnr?.medical_history) {
        for (const field in this.cardioQnr?.medical_history[key]) {
          if (this.cardioQnr?.medical_history[key][field]) {
            if (field === "other_personal_conditions") {
              this.personalHistoryArray.push("other");
            } else if (field === "other_family_conditions") {
              this.familyHistoryArray.push("other");
            } else {
              if (key === "personal_history") {
                this.personalHistoryArray.push(field);
              } else {
                this.familyHistoryArray.push(field);
              }
            }
          }
        }
      }
    },
    convertPhysicalActivityTypeArrayToObject(arr) {
      if (!this.cardioQnr?.lifestyle?.physical_activities) {
        return;
      }
      for (const item in this.cardioQnr?.lifestyle?.physical_activities) {
        this.cardioQnr.lifestyle.physical_activities[item] = arr.includes(item)
          ? true
          : false;
      }
    },
    convertCardioQnrArrayToObject(key, arr) {
      if (!(key in this.cardioQnr.medical_history)) {
        console.log("key not found in medical_history");
        return;
      }
      // Iterate through the medical history object and set the value of each field to true if it's in the provided array
      for (const item in this.cardioQnr.medical_history[key]) {
        if (!item.includes("other")) {
          this.cardioQnr.medical_history[key][item] = arr.includes(item)
            ? true
            : false;
        }
      }
      // check the array, if user doesn't select 'other' then clear the other field
      if (!arr.includes("other")) {
        let other_key_name =
          key === "personal_history"
            ? "other_personal_conditions"
            : "other_family_conditions";
        this.cardioQnr.medical_history[key][other_key_name] = "";
      }
    },
    dateConversion(dataType, date) {
      if (!date || !moment(date).isValid()) {
        return;
      }
      if (dataType === "birthdate") {
        this.setBirthdate(moment(date).format("MM/DD/YYYY"));
      }
    },
    setBirthdate(val) {
      this.birthdate = val;
      this.cardioQnr.basic_info.birthdate = moment(val, "MM/DD/YYYY").format(
        "YYYY-MM-DD"
      );
    },
  },
});
